import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Mail, Phone, Linkedin } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';

const OurTeam = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Team members
  const teamMembers = [
    {
      name: "Tamara Kraft",
      title: "Chief Executive Officer",
      bio: "Tamara serves as President and Majority Owner of BCS, providing executive leadership and strategic direction across the organization. She remains actively involved in all aspects of the business — from job walks and client coordination to operational oversight and long-term planning — ensuring every project aligns with company standards, client expectations, and sustainable growth objectives. BCS is a nationally certified Women's Business Enterprise (WBE) through the Women's Business Enterprise National Council (WBENC), certified by the Women's Business Enterprise Council — West (WBEC-West). Tamara is a proud member of WBEC-West, reinforcing BCS's commitment to diversity and excellence in the industrial services sector.",
      image: null,
      email: null,
      phone: null,
      linkedin: null
    },
    {
      name: "Jason Kraft",
      title: "President & General Manager",
      bio: "Jason serves as President of BCS, providing strategic leadership and operational direction across all divisions of the company. With extensive experience in industrial repair, protective coating systems, and infrastructure rehabilitation, he oversees project development, estimating, technical review, and field execution to ensure disciplined, specification-driven performance. Jason works closely with field leadership and strategic partners to deliver long-term asset protection solutions for municipal and industrial clients.",
      image: null,
      email: null,
      phone: null,
      linkedin: null
    },
    {
      name: "James Harris",
      title: "Head of Field Operations & Strategic Partner",
      bio: "James leads all field operations for BCS and serves as a Strategic Partner within the organization. He oversees every installation team, directing field execution, project coordination, safety standards, and quality control across active projects. With extensive experience in industrial repair and protective coating systems, James ensures that all work is performed in accordance with company standards, manufacturer specifications, and contract requirements. His leadership drives disciplined execution, operational efficiency, and consistent performance across every project site.",
      image: null,
      email: null,
      phone: null,
      linkedin: null
    },
    {
      name: "Jake Harris",
      title: "Senior Field Superintendent – Team Lead",
      bio: "Jake is a senior leader within BCS field operations, providing hands-on direction across installation activities and setting execution standards in the field. He oversees crew performance, site coordination, and adherence to project specifications and manufacturer requirements. His leadership reinforces accountability, safety, and disciplined workmanship across all active operations.",
      image: null,
      email: null,
      phone: null,
      linkedin: null
    },
    {
      name: "Daniel Curiel",
      title: "Senior Field Superintendent – Team Lead",
      bio: "Daniel plays a central role in BCS field operations, providing hands-on leadership and oversight of installation teams. He upholds company standards, contract requirements, and manufacturer guidelines to ensure safe, efficient, and high-quality execution. His experience and presence in the field contribute directly to the company's reputation for disciplined performance.",
      image: null,
      email: null,
      phone: null,
      linkedin: null
    },
    {
      name: "Ryan Harris",
      title: "Senior Field Superintendent – Team Lead",
      bio: "Ryan serves as a key member of the company's field leadership team, directing installation efforts and maintaining high standards of execution on site. He ensures proper surface preparation, environmental controls, and application procedures are consistently performed to specification. Ryan's field leadership supports operational efficiency and long-term system performance.",
      image: null,
      email: null,
      phone: null,
      linkedin: null
    },
    {
      name: "Charles Brown",
      title: "Senior Field Technician",
      bio: "Charles serves as a Senior Field Technician at BCS, bringing extensive hands-on experience in industrial repair and protective coating applications. He supports field operations across active projects, ensuring proper surface preparation, material handling, and application procedures are executed in accordance with project specifications and manufacturer guidelines. His technical proficiency and field discipline contribute to safe, efficient, and high-quality installations in demanding environments.",
      image: null,
      email: null,
      phone: null,
      linkedin: null
    },
    {
      name: "Dr. Aston Goldsworthy",
      title: "Strategic Growth Consultant",
      bio: "Aston serves as BCS's Strategic Growth Consultant, advising on long-term expansion, market positioning, and operational scaling. He works closely with leadership to identify growth opportunities, strengthen strategic direction, and ensure the company is positioned for sustainable expansion within the industrial and municipal sectors. His guidance supports disciplined growth aligned with the company's core capabilities and long-term objectives.",
      image: null,
      email: null,
      phone: null,
      linkedin: null
    },
    {
      name: "Daniel Kaufman",
      title: "Executive Industry Advisor",
      bio: "Daniel serves as Executive Industry Advisor to BCS, providing high-level guidance on industry relationships, strategic partnerships, and market expansion. Drawing on extensive sector experience and established professional networks, he supports leadership in identifying opportunities, strengthening alliances, and positioning the company for continued growth within the infrastructure and industrial markets.",
      image: null,
      email: null,
      phone: null,
      linkedin: null
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["about/our-team"]?.title} description={seoData["about/our-team"]?.description} path="/about/our-team" keywords={seoData["about/our-team"]?.keywords || []} />
      
      <PageHero
        badge="Our People"
        title="Meet the BCS Team"
        description="Dedicated professionals with decades of combined experience in industrial repair and protective coatings."
        backgroundKey="our-team"
        showButton={false}
      />

      {/* Team Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-4 bg-blue-100 text-blue-900">Leadership & Expertise</Badge>
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            The People Behind BCS
          </h2>
          <p className="text-lg text-gray-600">
            Our team brings together expertise in corrosion engineering, protective coatings, 
            and industrial maintenance. With hands-on experience across oil & gas, manufacturing, 
            marine, and infrastructure sectors, we understand the challenges you face and deliver 
            solutions that work.
          </p>
        </div>
      </section>

      {/* Team Grid */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <Card key={index} className="overflow-hidden hover:shadow-lg transition-shadow">
                {member.image && (
                  <div className="aspect-square">
                    <img 
                      src={member.image} 
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <CardContent className="p-6 text-center">
                  <h3 className="text-xl font-bold text-slate-900 mb-1">{member.name}</h3>
                  <p className="text-blue-900 font-medium mb-3">{member.title}</p>
                  {member.bio && <p className="text-gray-600 text-sm mb-4">{member.bio}</p>}
                  
                  {(member.email || member.phone || member.linkedin) && (
                    <div className="flex justify-center gap-3">
                      {member.email && (
                        <a 
                          href={`mailto:${member.email}`}
                          className="p-2 bg-slate-100 rounded-full hover:bg-blue-100 transition-colors"
                          title="Email"
                        >
                          <Mail size={18} className="text-slate-600" />
                        </a>
                      )}
                      {member.phone && (
                        <a 
                          href={`tel:${member.phone.replace(/[^0-9]/g, '')}`}
                          className="p-2 bg-slate-100 rounded-full hover:bg-blue-100 transition-colors"
                          title="Phone"
                        >
                          <Phone size={18} className="text-slate-600" />
                        </a>
                      )}
                      {member.linkedin && (
                        <a 
                          href={member.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-slate-100 rounded-full hover:bg-blue-100 transition-colors"
                          title="LinkedIn"
                        >
                          <Linkedin size={18} className="text-slate-600" />
                        </a>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">What Drives Us</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🎯</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Quality First</h3>
              <p className="text-gray-600">Every repair is done right the first time, with meticulous attention to surface preparation and application.</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🤝</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Client Partnership</h3>
              <p className="text-gray-600">We work alongside your team to understand challenges and deliver solutions that fit your operations.</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">⚡</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Rapid Response</h3>
              <p className="text-gray-600">When equipment fails, every hour counts. Our team is ready to mobilize 24/7 for emergency repairs.</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OurTeam;
